import {
  AccountData,
  Conversation,
  Follower,
  FollowerMembershipState,
  IMessage,
  TextTypes,
} from "@markit/common.types";
import "../../../css/GlobalStyles.css";
import { Colors } from "../../../utils/colors";
import LargePopupModalContainer from "../../Containers/LargePopupModalContainer";
import { useSelector, useDispatch } from "react-redux";
import { getAccountSlice } from "../../../redux/slices/accountSlice";
import { Icon } from "@iconify/react";
import ProfileItem from "../../ProfileItem";
import ConversationsMessagesContainer from "./ConversationsMessagesContainer";
import { CircularProgress } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API } from "../../../API";
import { getUserConversationRef } from "../../../utils/FirebaseUtils";
import { onSnapshot } from "../../../firebase";
import { uniqueVals } from "@markit/common.utils";
import { fetchSingleFollower } from "../../../utils/FetchSingleData";
import {
  fetchInitialConversationPresets,
  getConversationState,
} from "../../../redux/slices/conversationSlice";
import ConversationMessageInput from "./ConversationMessageInput";
import { useOnMount } from "../../../hooks/useOnMount";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";

type ConversationsSidebarModalProps = {
  conversationUser: AccountData;
  setConversationUser: (conversationUser: AccountData | undefined) => void;
  reopenProfilePreview?: () => void;
  fullProfileOpen: boolean;
};

const ConversationsSidebarModal = (props: ConversationsSidebarModalProps) => {
  const {
    conversationUser,
    setConversationUser,
    reopenProfilePreview,
    fullProfileOpen,
  } = props;
  const { loadedConversationPresets } = useSelector(getConversationState);
  const { accountData, followingAccountData, followersData } =
    useSelector(getAccountSlice);
  const dispatch = useDispatch();
  const [fetchedAllMessages, setFetchedAllMessages] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(true);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [messageCount, setMessageCount] = useState(15);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [followerData, setFollowerData] = useState<Follower | undefined>(
    undefined
  );

  const scrollableFeedRef = useRef<any>(null);

  const scrollToBottom = () => {
    scrollableFeedRef.current?.scrollToBottom();
  };

  useAsyncOnMount(async () => {
    const follower = await fetchSingleFollower(
      accountData.uid,
      conversationUser.uid,
      followersData
    );
    if (!loadedConversationPresets) {
      dispatch(fetchInitialConversationPresets());
    }
    setFollowerData(follower);
  });

  const followerMembershipPlan = useMemo(
    () =>
      followerData &&
      followerData.membershipState === FollowerMembershipState.ACTIVE
        ? accountData.membership.membershipPlans.find(
            (plan) => plan.id === followerData.membershipPlanId
          )
        : undefined,
    [accountData.membership.membershipPlans, followerData]
  );

  const fetchConversationMessages = useCallback(
    async (increment: boolean) => {
      if (conversationUser) {
        setLoadingMoreMessages(true);
        // Messages live under the participant's uid, whether or not the thread has a
        // conversation doc yet (a thread that only received mass texts doesn't)
        const response = await API.creatorText.fetchUserConversationMessages({
          conversationId: conversationUser.uid,
          twilioUser: accountData,
          attendeeUser: conversationUser,
          messageLimit: increment ? messageCount + 15 : messageCount,
        });
        const { messages } = response;
        if (messages.length === 0) {
          setFetchedAllMessages(true);
        }
        setMessageCount(increment ? messageCount + 15 : messageCount);
        const sortedMessages = messages.sort((x: IMessage, y: IMessage) => {
          return (
            new Date(x.createdAt).getTime() - new Date(y.createdAt).getTime()
          );
        });
        setMessages(uniqueVals(sortedMessages));
        setLoadingConversation(false);
        setLoadingMoreMessages(false);
      }
    },
    [accountData, conversationUser, messageCount]
  );

  const newIMessage = useCallback(
    (convoData: Conversation) => {
      const iMessage: IMessage = {
        _id: convoData.recentMessage.twilioMessageId,
        text: convoData.recentMessage.text ?? "",
        photoURL: convoData.recentMessage.photoURL ?? "",
        createdAt: new Date(convoData.recentTimestamp),
        user: {
          _id: convoData.recentMessage.sentBy,
          name: followingAccountData.find(
            (m) => m.uid === convoData.recentMessage.sentBy
          )?.fullName,
          avatar: followingAccountData.find(
            (m) => m.uid === convoData.recentMessage.sentBy
          )?.profilePicURL,
        },
        type: TextTypes.CREATORMESSAGE,
      };
      return iMessage;
    },
    [followingAccountData]
  );

  useOnMount(() => {
    const unsubscribe = onSnapshot(
      getUserConversationRef(accountData.uid, conversationUser.uid),
      (snapshot) => {
        const conversationData = snapshot.data();
        if (!conversationData) {
          setLoadingConversation(false);
          return;
        }
        const iMessage: IMessage = newIMessage(conversationData);
        if (iMessage.text !== "" || iMessage.photoURL !== "") {
          setMessages((prev) => {
            if (conversationData.recentMessage.sentBy !== accountData.uid) {
              const index = prev.findIndex((c) => c._id === iMessage._id);
              if (index === -1) {
                prev = prev.concat(iMessage);
              } else {
                prev.splice(index, 1, iMessage);
              }
            } else {
              prev = prev.concat(iMessage);
            }
            return uniqueVals(prev, (message) => message._id.toString());
          });
        }
      }
    );

    return () => unsubscribe();
  });

  useEffect(() => {
    fetchConversationMessages(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationUser]);

  return (
    <>
      <LargePopupModalContainer
        showModal={conversationUser !== undefined}
        headerComp={
          <div>
            <div
              className="LargePopupPanelClose"
              onClick={() => {
                setConversationUser(undefined);
                if (reopenProfilePreview) {
                  reopenProfilePreview();
                }
              }}
            >
              <Icon
                icon={fullProfileOpen ? "ion:chevron-back" : "mdi:close"}
                height={24}
              />
            </div>
            <div style={{ margin: "14px -14px 14px" }}>
              <hr />
            </div>
          </div>
        }
        subHeaderComp={
          <ProfileItem
            profileItem={conversationUser}
            onItemPress={() => {}}
            disabled
            hideViewIcon
            subtext={
              followerMembershipPlan ? (
                <div className="AlignedRow" style={{ gap: 4, paddingTop: 4 }}>
                  <Icon
                    icon="ion:ribbon"
                    height={12}
                    style={{ color: Colors.GRAY1 }}
                  />
                  <span className="smallBodySubtext">
                    {followerMembershipPlan.name}
                  </span>
                </div>
              ) : undefined
            }
          />
        }
        valueComp={
          loadingConversation ? (
            <div
              className="ColumnCentering"
              style={{ gap: 24, height: "60vh" }}
            >
              <CircularProgress style={{ color: Colors.BLACK }} size={38} />
              <div
                className="ColumnNormal"
                style={{ gap: 14, textAlign: "center" }}
              >
                <span className="sectionTitle">Loading Chat</span>
                <span className="bodySubtext">
                  This will only take a second
                </span>
              </div>
            </div>
          ) : (
            <div
              className="ColumnNormal"
              style={{
                margin: "0px -20px 10px",
                height: "calc(100vh - 190px)",
              }}
            >
              <ConversationsMessagesContainer
                messages={messages}
                userId={accountData.uid}
                conversationsMainUser={conversationUser}
                fetchedAllMessages={fetchedAllMessages}
                fetchConversationMessages={() =>
                  fetchConversationMessages(true)
                }
                loadingMoreMessages={loadingMoreMessages}
                messageBubbleAltWidth={"70%"}
                scrollableFeedRef={scrollableFeedRef}
              />
              <div className="AlignedRow" style={{ paddingTop: 10 }}>
                <ConversationMessageInput
                  conversationsMainUser={conversationUser}
                  scrollToBottom={scrollToBottom}
                  loadingConversation={loadingConversation}
                  messages={messages}
                />
              </div>
            </div>
          )
        }
      />
    </>
  );
};

export default ConversationsSidebarModal;
